import { useState } from "react";
import moment from "moment/moment";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Button from "../UI/Button";
import Card from "../UI/Card";
import Callout from "../UI/Callout";
// @ts-expect-error pure js
import Spinner from "../UI/Spinner";
// @ts-expect-error pure js
import TextInputStandalone from "../UI/TextInputStandalone";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
// @ts-expect-error pure js
import Radio from "../UI/Radio";
import FormikSubmitButton from "../UI/FormikSubmitButton";
import CopyToClipboard from "../UI/CopyToClipboard";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import ModalDialog from "../UI/ModalDialog";
import Text from "../UI/Text";
import Status from "../UI/Status";
import { Table } from "../UI/Table";
import useMCPTokens from "../../hooks/use-mcp-tokens";
import type { CreateTokenInput } from "../../hooks/use-mcp-tokens";
import type { MCPToken, CreatedTokenResponse } from "../../models/mcpToken";
import styles from "./MCPTokensList.module.css";

const MCP_URL = `${import.meta.env.VITE_BACKEND_URL}/mcp`;

export default function MCPTokensList() {
  const {
    tokens,
    error: fetchError,
    isLoading,
    createToken,
    revokeToken,
  } = useMCPTokens();
  const [actionError, setActionError] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createdToken, setCreatedToken] = useState<CreatedTokenResponse | null>(
    null,
  );

  const error = actionError || (fetchError ? handleAPIError(fetchError) : "");

  async function handleRevoke(token: MCPToken) {
    if (!window.confirm(`Révoquer le token "${token.name}" ?`)) return;
    try {
      setActionError("");
      await revokeToken(token.id);
    } catch (err) {
      setActionError(handleAPIError(err));
    }
  }

  return (
    <Card>
      <Text variant="kilo" weight="bold">
        Intégration MCP (IA)
      </Text>
      <Text color="muted" className={styles.intro}>
        Connectez Claude.ai, ChatGPT, Crisp Hugo ou tout autre assistant
        compatible MCP à vos données Qalimo.
      </Text>

      <div className={styles.section}>
        <Text weight="medium" className={styles.fieldLabel}>
          URL du serveur MCP :
        </Text>
        <div className={styles.inputRow}>
          <TextInputStandalone
            disabled
            value={MCP_URL}
            className={styles.tokenInput}
          />
          <CopyToClipboard toCopy={MCP_URL} />
        </div>
      </div>

      <div className={styles.tokensHeader}>
        <Text weight="medium">Vos tokens</Text>
        <Button onClick={() => setIsCreateOpen(true)}>Créer un token</Button>
      </div>

      <Callout variant="danger" className={styles.errorCard}>
        {error}
      </Callout>

      {isLoading ? (
        <div className={styles.loading}>
          <Spinner />
        </div>
      ) : tokens.length > 0 ? (
        <Table variant="card" className={styles.table}>
          <Table.Header>
            <Table.Column>Nom</Table.Column>
            <Table.Column>Scopes</Table.Column>
            <Table.Column>Créé</Table.Column>
            <Table.Column>Utilisé</Table.Column>
            <Table.Column>Expire</Table.Column>
            <Table.Column />
          </Table.Header>
          <Table.Body>
            {tokens.map((token) => (
              <Table.Row key={token.id}>
                <Table.Cell>{token.name}</Table.Cell>
                <Table.Cell>
                  <div className={styles.scopeList}>
                    {token.scopes.map((s) => (
                      <Status
                        key={s}
                        as="span"
                        color={s === "write" ? "warning" : "success"}
                        text={s}
                      />
                    ))}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  {moment(token.created_at).format("DD/MM/YYYY")}
                </Table.Cell>
                <Table.Cell>
                  {token.last_used_at
                    ? moment(token.last_used_at).fromNow()
                    : "jamais"}
                </Table.Cell>
                <Table.Cell>
                  {token.expires_at
                    ? moment(token.expires_at).format("DD/MM/YYYY")
                    : "jamais"}
                </Table.Cell>
                <Table.Cell>
                  {token.revoked_at ? (
                    <Status as="span" color="muted" text="révoqué" />
                  ) : (
                    <Button
                      variant="secondary"
                      onClick={() => handleRevoke(token)}
                    >
                      Révoquer
                    </Button>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      ) : (
        <Text color="muted" className={styles.emptyState}>
          Aucun token pour l'instant.
        </Text>
      )}

      <CreateTokenDialog
        open={isCreateOpen}
        onOpenChange={(open) => {
          setIsCreateOpen(open);
          if (!open) setCreatedToken(null);
        }}
        createdToken={createdToken}
        createToken={createToken}
        onCreated={setCreatedToken}
      />
    </Card>
  );
}

interface CreateTokenFormValues {
  name: string;
  scope: "read" | "read+write";
  expiresDays: string;
}

const createTokenIV: CreateTokenFormValues = {
  name: "",
  scope: "read",
  expiresDays: "365",
};

const createTokenSchema = Yup.object({
  name: Yup.string().trim().required("Donnez un nom à votre token."),
  scope: Yup.string().oneOf(["read", "read+write"]).required(),
  expiresDays: Yup.number()
    .transform((value, original) =>
      String(original).trim() === "" ? undefined : value,
    )
    .typeError("Indiquez un nombre de jours valide.")
    .integer("Indiquez un nombre de jours valide.")
    .positive("Indiquez un nombre de jours valide."),
});

interface CreateTokenDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  createdToken: CreatedTokenResponse | null;
  createToken: (input: CreateTokenInput) => Promise<CreatedTokenResponse>;
  onCreated: (resp: CreatedTokenResponse) => void;
}

function CreateTokenDialog({
  open,
  onOpenChange,
  createdToken,
  createToken,
  onCreated,
}: CreateTokenDialogProps) {
  const [error, setError] = useState("");

  async function submitHandler(
    values: CreateTokenFormValues,
    { resetForm }: { resetForm: () => void },
  ) {
    setError("");
    try {
      const scopes =
        values.scope === "read+write" ? ["read", "write"] : ["read"];
      const input: CreateTokenInput = { name: values.name.trim(), scopes };
      const days = parseInt(values.expiresDays, 10);
      if (Number.isFinite(days) && days > 0) input.expires_days = days;
      const response = await createToken(input);
      onCreated(response);
      resetForm();
    } catch (err) {
      setError(handleAPIError(err));
    }
  }

  return (
    <Formik
      initialValues={createTokenIV}
      validationSchema={createTokenSchema}
      onSubmit={submitHandler}
    >
      {({ resetForm }) => (
        <ModalDialog
          open={open}
          header={createdToken ? "Token créé" : "Créer un token MCP"}
          size="sm"
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              resetForm();
              setError("");
            }
            onOpenChange(nextOpen);
          }}
        >
          {createdToken ? (
            <div className={styles.form}>
              <Text color="muted">
                Copiez ce token maintenant : il ne sera plus jamais affiché.
              </Text>
              <div className={styles.tokenReveal}>
                <TextInputStandalone
                  disabled
                  value={createdToken.token}
                  className={styles.tokenInput}
                />
                <CopyToClipboard toCopy={createdToken.token} />
              </div>
              <div className={styles.dialogActions}>
                <Button variant="secondary" onClick={() => onOpenChange(false)}>
                  Fermer
                </Button>
              </div>
            </div>
          ) : (
            <Form className={styles.form}>
              <TextInput
                label="Nom"
                name="name"
                placeholder="Mon token Claude.ai"
                maxLength={100}
                marginForm=""
                mandatory
              />

              <div className={styles.scopeRadios}>
                <Text weight="medium">Permissions</Text>
                <Radio
                  name="scope"
                  value="read"
                  label="Lecture seule (recommandé)"
                />
                <Radio
                  name="scope"
                  value="read+write"
                  label="Lecture + écriture (créer des quittances, des écritures, etc.)"
                />
              </div>

              <TextInput
                type="number"
                label="Expire dans (jours)"
                name="expiresDays"
                placeholder="365"
                subText="Laissez vide pour ne jamais expirer."
                min={1}
                marginForm=""
              />

              <Callout variant="danger" className={styles.errorCard}>
                {error}
              </Callout>

              <div className={styles.dialogActions}>
                <Button
                  variant="secondary"
                  onClick={() => onOpenChange(false)}
                  type="button"
                >
                  Annuler
                </Button>
                <FormikSubmitButton>Créer</FormikSubmitButton>
              </div>
            </Form>
          )}
        </ModalDialog>
      )}
    </Formik>
  );
}
